import { formatToDollar, SuretyAccountId } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	type AdminAccountCompany,
	computeAdjustedFsr,
	useAdminRunCompanyCreditReport,
} from "@portal/api/account/admin";
import { CreditReportActionsMenu } from "@portal/components/features/account/credit-report";
import { Icon, PartialIndicator, Text } from "@portal/components/primitives";
import { DynamicDataTable } from "@portal/components/ui/DynamicDataTable/DynamicDataTable";
import { DynamicDataTableColumn } from "@portal/components/ui/DynamicDataTable/types";
import { LineItem } from "@portal/components/ui/LineItem";
import { PortaledTooltip } from "@portal/components/ui/Tooltip";
import { DateTime } from "luxon";
import { FC, useCallback, useId, useMemo, useState } from "react";
import { formatEmpty } from "../../../../../utils";
import { CompanyCreditNameCell } from "./CompanyCreditNameCell";
import { CompanyCreditReportModal } from "./credit-report/CompanyCreditReportModal";
import { companyCreditFailureMessage } from "./credit-report/utils/companyCreditFailureMessage";
import { experianFsrExclusionMessage } from "./credit-report/utils/experianFsrExclusionMessage";

type CompanyCreditReport = AdminAccountCompany["creditReports"][number];

type CompanyCreditReportRow = {
	name: string;
	success: CompanyCreditReport["success"];
	fsr: NonNullable<CompanyCreditReport["creditProfile"]>["experianFsr"];
	lienBalance: NonNullable<CompanyCreditReport["creditProfile"]>["lienBalance"];
	judgmentBalance: NonNullable<CompanyCreditReport["creditProfile"]>["judgmentBalance"];
	bankruptcy: NonNullable<CompanyCreditReport["creditProfile"]>["bankruptcy"];
	createdAt: CompanyCreditReport["createdAt"];
	creditReport: CompanyCreditReport;
	company: AdminAccountCompany;
};

const buildCompanyCreditReportColumns = ({
	onRunCreditReport,
	runCreditReportPending,
}: {
	onRunCreditReport: (company: AdminAccountCompany) => void;
	runCreditReportPending: boolean;
}): DynamicDataTableColumn<CompanyCreditReportRow>[] => [
	{
		id: "actions",
		header: "Actions",
		size: 80,
		cell: ({ row }) => (
			<div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
				<CreditReportActionsMenu
					testId={`company-credit-report-actions-${row.original.company.id}`}
					confirmMessage={`Run a commercial credit report for ${row.original.company.name}? This pulls a fresh report from Experian.`}
					onRunCreditReport={() => onRunCreditReport(row.original.company)}
					runCreditReportPending={runCreditReportPending}
				/>
			</div>
		),
	},
	{
		id: "timestampStatus",
		header: "Date Pulled",
		accessorKey: "createdAt",
		size: 140,
		minSize: 140,
		maxSize: 140,
		enableSorting: true,
		cell: ({ row }) => {
			const createdAt = row.original.createdAt;
			const exclusionMessage = experianFsrExclusionMessage(row.original.creditReport);
			const expired =
				createdAt !== undefined
					? DateTime.fromISO(createdAt).diffNow("years").years < -1
					: undefined;
			return (
				<div className="flex items-center space-x-[8px]">
					{createdAt === undefined ? (
						<Icon
							type="x-circle"
							size="small"
							color="secondary"
							className="stroke-[1.75]"
							data-testid="company_credit_pull_missing"
						/>
					) : expired ? (
						<PartialIndicator active testId="company_credit_pull_expired" />
					) : row.original.success && Option.isSome(row.original.fsr) ? (
						<Icon
							type="check-circle"
							size="small"
							color="confirm"
							className="stroke-[1.75]"
							data-testid="company_credit_pull_success"
						/>
					) : row.original.success ? (
						<UnscoredPullIcon message={exclusionMessage} />
					) : (
						<FailedPullIcon message={companyCreditFailureMessage(row.original.creditReport)} />
					)}
					<span>
						{createdAt !== undefined ? DateTime.fromISO(createdAt).toFormat("MM/dd/yyyy") : "--"}
					</span>
				</div>
			);
		},
	},
	{
		id: "companyName",
		accessorKey: "name",
		header: "Company",
		size: 280,
		enableSorting: true,
		enableResizing: true,
		cell: ({ row }) => (
			<CompanyCreditNameCell
				companyId={row.original.company.id}
				name={row.original.company.name}
				creditReportName={row.original.creditReport?.creditProfile?.name}
			/>
		),
	},
	{
		id: "fsr",
		accessorKey: "fsr",
		header: "FSR",
		size: 75,
		minSize: 75,
		enableSorting: true,
	},
	{
		id: "lienBalance",
		accessorKey: "lienBalance",
		header: "Lien",
		size: 120,
		minSize: 100,
		enableSorting: true,
		cell: ({ row }) => formatEmpty(formatToDollar(row.original.lienBalance)),
	},
	{
		id: "judgmentBalance",
		accessorKey: "judgmentBalance",
		header: "Judgment",
		size: 120,
		minSize: 100,
		enableSorting: true,
		cell: ({ row }) => formatEmpty(formatToDollar(row.original.judgmentBalance)),
	},
	{
		id: "bankruptcy",
		accessorKey: "bankruptcy",
		header: "Bankruptcy",
		size: 120,
		minSize: 100,
		enableSorting: true,
		cell: ({ row }) =>
			row.original.bankruptcy !== undefined
				? row.original.bankruptcy
					? "Yes"
					: "No"
				: formatEmpty(row.original.bankruptcy),
	},
];

const UnscoredPullIcon: FC<{ message: string | undefined }> = ({ message }) => {
	const tooltipId = useId();

	return (
		<>
			<Icon
				type="information-circle"
				size="small"
				color="secondary"
				className="stroke-[1.75]"
				aria-label={message}
				data-testid="company_credit_pull_unscored"
				data-tooltip-id={message ? tooltipId : undefined}
				data-tooltip-content={message}
			/>
			{message && <PortaledTooltip id={tooltipId} className="max-w-[280px] amp-mask sentry-mask" />}
		</>
	);
};

const FailedPullIcon: FC<{ message: string | undefined }> = ({ message }) => {
	const tooltipId = useId();

	return (
		<>
			<Icon
				type="x-mark"
				size="small"
				color="danger"
				filled
				className="stroke-[1.75]"
				aria-label={message}
				data-testid="company_credit_pull_failure"
				data-tooltip-id={message ? tooltipId : undefined}
				data-tooltip-content={message}
			/>
			{message && <PortaledTooltip id={tooltipId} className="max-w-[280px]" />}
		</>
	);
};

type AdminAccountCompaniesCreditProps = {
	accountId: SuretyAccountId;
	companies: AdminAccountCompany[];
	hideHeading?: boolean;
};

export const AdminAccountCompaniesCredit: FC<AdminAccountCompaniesCreditProps> = ({
	accountId,
	companies,
	hideHeading = false,
}) => {
	const [companyCreditReportModal, setCompanyCreditReportModal] = useState<
		| {
				company: AdminAccountCompany;
				creditReport: CompanyCreditReport | undefined;
		  }
		| undefined
	>(undefined);

	const { runCompanyCreditReport, runCompanyCreditReportPending } =
		useAdminRunCompanyCreditReport(accountId);

	const companyCreditReportColumns = useMemo(
		() =>
			buildCompanyCreditReportColumns({
				onRunCreditReport: runCompanyCreditReport,
				runCreditReportPending: runCompanyCreditReportPending,
			}),
		[runCompanyCreditReport, runCompanyCreditReportPending],
	);

	const companyCreditReportsTableData = useMemo(
		() =>
			companies
				.filter((c) => c.config.contract.includeInUnderwriting)
				.map((company) => {
					const cr = company.creditReports[0];
					return {
						name: company.name,
						success: cr?.success,
						fsr: cr?.creditProfile?.experianFsr,
						lienBalance: cr?.creditProfile?.lienBalance,
						judgmentBalance: cr?.creditProfile?.judgmentBalance,
						bankruptcy: cr?.creditProfile?.bankruptcy,
						createdAt: cr?.createdAt,
						creditReport: cr,
						company,
					};
				}),
		[companies],
	);

	const adjustedFsr = useMemo(() => computeAdjustedFsr(companies), [companies]);

	const handleRowClick = useCallback(
		(row: CompanyCreditReportRow) =>
			setCompanyCreditReportModal({
				company: row.company,
				creditReport: row.creditReport,
			}),
		[],
	);

	return (
		<div
			className="flex flex-col space-y-[15px]"
			data-amp-mask
			data-testid="admin_account_companies_credit"
		>
			{!hideHeading && (
				<Text type="caption" color="tertiary" className="italic">
					Company Credit Reports
				</Text>
			)}
			<LineItem label="Adjusted FSR" className="px-[20px] pb-[5px]">
				{formatEmpty(adjustedFsr)}
			</LineItem>
			<DynamicDataTable
				name="admin_account_company_credit_reports"
				tableClassName="shadow-sm"
				columns={companyCreditReportColumns}
				data={companyCreditReportsTableData}
				onRowClick={handleRowClick}
				expandToContent
			/>
			{companyCreditReportModal && (
				<CompanyCreditReportModal
					company={companyCreditReportModal.company}
					report={companyCreditReportModal.creditReport}
					onClose={() => setCompanyCreditReportModal(undefined)}
				/>
			)}
		</div>
	);
};
